// Conversation Service
// Thin client wrapper around callable messaging backend.

import { auth } from '../firebase.js';
import {
  callMessagingFunction,
  normalizeConversation,
} from './callableMessagingClient.js';

const requireCurrentUser = (userId) => {
  const currentUserId = auth.currentUser?.uid || null;
  if (!currentUserId) {
    return {
      success: false,
      error: 'Authentication required',
    };
  }

  if (userId && currentUserId !== userId) {
    return {
      success: false,
      error: 'Sender identity does not match the signed-in user',
    };
  }

  return { success: true, currentUserId };
};

const normalizeConversationResponse = (conversation = null) => normalizeConversation(conversation);

export const createConversation = async ({
  id = null,
  participants = [],
  childId,
  type,
  title = null,
  createdBy,
  conversationType = null,
  childName = null,
}) => {
  try {
    const authCheck = requireCurrentUser(createdBy, 'createConversation');
    if (!authCheck.success) return authCheck;

    if (!childId) {
      return {
        success: false,
        error: 'childId is required',
      };
    }

    const semanticConversationType = conversationType || (type === 'direct' ? 'direct' : 'custom_group');

    if (semanticConversationType === 'child_care_team') {
      const result = await callMessagingFunction('createOrOpenDefaultCareTeamConversation', {
        childId,
        childName,
      });

      return {
        success: true,
        conversationId: result.conversationId || id || null,
        conversation: normalizeConversationResponse(result.conversation),
        isExisting: Boolean(result.isExisting),
        message: result.message || 'Conversation opened successfully',
      };
    }

    const result = await callMessagingFunction('createCustomChildConversation', {
      childId,
      participantIds: Array.isArray(participants) ? participants : [],
      title,
      childName,
    });

    return {
      success: true,
      conversationId: result.conversationId || id || null,
      conversation: normalizeConversationResponse(result.conversation),
      isExisting: Boolean(result.isExisting),
      message: result.message || 'Conversation created successfully',
    };
  } catch (error) {
    console.error('Error in createConversation:', error);
    return {
      success: false,
      error: error.message || 'Failed to create conversation',
    };
  }
};

export const getConversations = async (userId, options = {}) => {
  try {
    const authCheck = requireCurrentUser(userId, 'getConversations');
    if (!authCheck.success) return authCheck;

    const result = await callMessagingFunction('listChildConversations', {
      childId: options.childId || null,
      limit: Number(options.limit) || 50,
    });

    const conversations = Array.isArray(result.conversations)
      ? result.conversations.map((conversation) => normalizeConversationResponse(conversation)).filter(Boolean)
      : [];

    return {
      success: true,
      conversations,
      totalCount: conversations.length,
      message: result.message || `Found ${conversations.length} conversations`,
    };
  } catch (error) {
    console.error('Error in getConversations:', error);
    return {
      success: false,
      error: error.message || 'Failed to get conversations',
    };
  }
};

export const getConversationById = async (conversationId, userId) => {
  try {
    const authCheck = requireCurrentUser(userId, 'getConversationById');
    if (!authCheck.success) return authCheck;
    if (!conversationId) {
      return {
        success: false,
        error: 'Conversation ID is required',
      };
    }

    const result = await callMessagingFunction('getConversationById', { conversationId });
    return {
      success: true,
      conversation: normalizeConversationResponse(result.conversation),
      message: result.message || 'Conversation retrieved successfully',
    };
  } catch (error) {
    console.error('Error in getConversationById:', error);
    return {
      success: false,
      error: error.message || 'Failed to get conversation',
    };
  }
};

export const updateConversation = async (conversationId, updates, userId) => {
  try {
    const authCheck = requireCurrentUser(userId, 'updateConversation');
    if (!authCheck.success) return authCheck;
    if (!conversationId) {
      return {
        success: false,
        error: 'Conversation ID is required',
      };
    }

    const result = await callMessagingFunction('updateConversation', {
      conversationId,
      updates: updates || {},
    });

    return {
      success: Boolean(result.success !== false),
      message: result.message || 'Conversation updated successfully',
    };
  } catch (error) {
    console.error('Error in updateConversation:', error);
    return {
      success: false,
      error: error.message || 'Failed to update conversation',
    };
  }
};

export const deactivateConversation = async (conversationId, userId) => {
  try {
    const authCheck = requireCurrentUser(userId, 'deactivateConversation');
    if (!authCheck.success) return authCheck;
    if (!conversationId) {
      return {
        success: false,
        error: 'Conversation ID is required',
      };
    }

    const result = await callMessagingFunction('archiveConversation', {
      conversationId,
    });

    return {
      success: Boolean(result.success !== false),
      message: result.message || 'Conversation archived successfully',
    };
  } catch (error) {
    console.error('Error in deactivateConversation:', error);
    return {
      success: false,
      error: error.message || 'Failed to deactivate conversation',
    };
  }
};

export const getOrCreateChildCareTeamConversation = async ({ childId, childName, createdBy }) => {
  try {
    const authCheck = requireCurrentUser(createdBy, 'getOrCreateChildCareTeamConversation');
    if (!authCheck.success) return authCheck;
    if (!childId) {
      return {
        success: false,
        error: 'childId is required',
      };
    }

    const result = await callMessagingFunction('createOrOpenDefaultCareTeamConversation', {
      childId,
      childName,
    });

    return {
      success: true,
      conversationId: result.conversationId || null,
      conversation: normalizeConversationResponse(result.conversation),
      isExisting: Boolean(result.isExisting),
      message: result.message || 'Conversation opened successfully',
    };
  } catch (error) {
    console.error('Error in getOrCreateChildCareTeamConversation:', error);
    return {
      success: false,
      error: error.message || 'Failed to open child conversation',
    };
  }
};
